// Tapestry rendering functions
import { colors } from "../styles/colors";
export const MIN_FONT_SIZE = 24;
export const MAX_FONT_SIZE = 120;
const textAnchor = "middle";
const svgNS = "http://www.w3.org/2000/svg";
const isSafari =
  navigator.userAgent &&
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
  navigator.userAgent.indexOf("CriOS") === -1 &&
  navigator.userAgent.indexOf("FxiOS") === -1;
const SAFARI_SCALAR = 0.65;

function scaleToRange(num, inMin, inMax, outMin, outMax) {
  return ((num - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
}

function getTextDimensions(textContent, fontSize, fontFamily = 'Hand1-Regular') {
  const dummySVG = document.createElementNS(svgNS, "svg");
  const dummySVGText = document.createElementNS(svgNS, "text");
  dummySVGText.setAttribute("font-family", `'${fontFamily}', Arial, Helvetica, sans-serif`);
  dummySVGText.setAttribute("font-size", fontSize + "px");
  dummySVGText.textContent = textContent;
  dummySVG.appendChild(dummySVGText);
  document.body.appendChild(dummySVG);
  const measurement = {
    // hacky fix for Safari SVG render sizing discrepancies
    width: dummySVGText.getBBox().width * (isSafari ? SAFARI_SCALAR : 1),
    height: dummySVGText.getBBox().height,
  };
  document.body.removeChild(dummySVG);
  return measurement;
}

export function createText({
  textContent,
  x,
  y,
  fontSize,
  maxWidth,
  maxHeight,
  color,
  isKeySentence,
  isTitle,
  eventID,
}) {
  const texts = [];
  const lineHeightScalar = scaleToRange(fontSize, MAX_FONT_SIZE * 0.1, MAX_FONT_SIZE * 0.375, 1, 0.8);
  const words = textContent
    .replace(/\r?\n|\r/g, " ")
    .split(" ")
    .map((t) => {
      const bbox = getTextDimensions(t, fontSize);
      return { text: t.trim(), boundingBox: bbox };
    });
  const space = getTextDimensions(" ", fontSize).width;
  const lines = [
    {
      width: words[0].boundingBox.width,
      height: words[0].boundingBox.height * lineHeightScalar,
      words: [words[0]],
    },
  ];
  for (let i = 1; i < words.length; i++) {
    const line = lines[lines.length - 1];
    if (line.width + space + words[i].boundingBox.width > (maxWidth || Infinity)) {
      lines.push({
        width: words[i].boundingBox.width,
        height: words[i].boundingBox.height * lineHeightScalar,
        words: [words[i]],
      });
    } else {
      line.width += space + words[i].boundingBox.width;
      if (words[i].boundingBox.height > line.height) {
        line.height = words[i].boundingBox.height * lineHeightScalar;
      }
      line.words.push(words[i]);
    }
  }
  const totalHeight = lines.reduce((acc, curr) => acc + curr.height, 0);
  if (maxHeight && totalHeight > maxHeight) {
    return null;
  }
  let totalWidth = 0;
  // let yPos = y - totalHeight / 2
  lines.forEach((line) => {
    if (line.width > totalWidth) {
      totalWidth = line.width;
    }
    // yPos += line.height / 2
    const textData = {
      text: line.words.map((w) => w.text).join(" "),
      fulltext: textContent,
      width: line.width,
      height: line.height,
      color: color || colors.black,
    };
    if (isTitle) {
      textData.dominantBaseline = "middle";
    }
    texts.push(textData);
    // yPos += line.height / 2
  });
  const textObject = {
    position: { x, y },
    lines: lines.length,
    fontSize,
    text: textContent,
    texts,
    renderStyle: {
      width: totalWidth,
      height: totalHeight,
    },
    group: null,
    color: color || colors.black,
    isKeySentence,
    eventID,
  };
  textObject.texts.forEach((t) => {
    t.renderStyle = textObject.renderStyle;
  });
  return textObject;
}

export function createTextpath({
  id,
  container,
  textContent,
  fontSize,
  color,
  dropShadow,
  className,
  textpathLength,
  lines = 1,
  defaultOffset = 0,
  lineHeightScalar = 1,
  editable = false,
  eventID,
  fontFamily = 'Hand1-Regular',
}) {
  const groupContainer = document.createElementNS(svgNS, "g");
  if (container) {
    container.appendChild(groupContainer);
  }
  if (eventID) {
    groupContainer.id = "phrase-" + eventID;
    groupContainer.setAttribute("fulltext", textContent);
    groupContainer.addEventListener("click", () =>
      window.dispatchEvent(
        new CustomEvent("selectText", {
          detail: { text: textContent },
        })
      )
    );
  }
  const wordsPerLine = [];
  const words = textContent.split(" ");

  // make sure none of the words are individually longer than the available textpath length
  let safeFontSize = fontSize;
  const longestWord = Math.max(...words.map((word) => getTextDimensions(word + " ", safeFontSize).width));
  if (longestWord > textpathLength) {
    safeFontSize *= textpathLength / longestWord;
  }

  // Pre-compute word widths for balanced distribution across lines
  const wordWidths = words.map((word, idx) =>
    getTextDimensions(word + (idx === words.length - 1 ? "" : " "), safeFontSize).width
  );

  // Compute line break points (wordBreaks[i] = starting word index for line i)
  const wordBreaks = [0];
  if (lines > 1 && textpathLength) {
    // Greedy pass: fill each line to capacity
    let wIdx = 0;
    for (let i = 0; i < lines - 1; i++) {
      let lineLen = 0;
      while (wIdx < words.length && lineLen + (wordWidths[wIdx] ?? 0) <= textpathLength) {
        lineLen += wordWidths[wIdx];
        wIdx++;
      }
      wordBreaks.push(wIdx);
    }

    // Rebalance: shift words from compressed lines to lighter adjacent lines
    const getLineWidth = (li) => {
      const start = wordBreaks[li];
      const end = li < lines - 1 ? wordBreaks[li + 1] : words.length;
      let w = 0;
      for (let j = start; j < end; j++) w += wordWidths[j];
      return w;
    };
    let shifted = true;
    while (shifted) {
      shifted = false;
      for (let i = lines - 1; i > 0; i--) {
        const currWidth = getLineWidth(i);
        if (currWidth <= textpathLength) continue;
        const prevWidth = getLineWidth(i - 1);
        if (currWidth <= prevWidth) continue;
        const lineEnd = i < lines - 1 ? wordBreaks[i + 1] : words.length;
        if (wordBreaks[i] + 1 >= lineEnd) continue;
        const oldMax = Math.max(currWidth, prevWidth);
        wordBreaks[i]++;
        const newMax = Math.max(getLineWidth(i), getLineWidth(i - 1));
        if (newMax < oldMax) {
          shifted = true;
        } else {
          wordBreaks[i]--;
        }
      }
    }

    // Dangler prevention: avoid single-word last line
    const lastLineWords = words.length - wordBreaks[lines - 1];
    if (lastLineWords === 1 && words.length > 3) {
      const prevStart = lines > 2 ? wordBreaks[lines - 2] : 0;
      if (wordBreaks[lines - 1] - prevStart > 1) {
        wordBreaks[lines - 1]--;
      }
    }
  }

  for (let i = 0; i < lines; i++) {
    const start = wordBreaks[i];
    const end = i < lines - 1 ? (wordBreaks[i + 1] ?? words.length) : words.length;
    const subtext = (lines === 1 || !textpathLength) ? textContent : words.slice(start, end).join(" ");
    wordsPerLine[i] = end - start;
    if (container && id) {
      const text = document.createElementNS(svgNS, "text");
      if (eventID) {
        text.addEventListener("click", () =>
          window.dispatchEvent(
            new CustomEvent("showPhraseOptions", {
              detail: { eventID },
            })
          )
        );
      }
      text.setAttribute("font-size", safeFontSize + "px");
      text.setAttribute("font-family", fontFamily);
      text.setAttribute("text-anchor", textAnchor);
      const yOffset = safeFontSize * lineHeightScalar * i + defaultOffset;
      if (yOffset) {
        text.setAttribute("transform", `translate(0, ${yOffset})`);
      }
      if (editable) {
        text.setAttribute("class", "editable");
      }
      groupContainer.setAttribute(
        "class",
        (editable ? "editable-textpath " : "") + (className ? className + "-container" : "")
      );
      const textpath = document.createElementNS(svgNS, "textPath");
      if (color) {
        textpath.setAttribute("fill", color);
      }
      if (dropShadow) {
        textpath.setAttribute("filter", `drop-shadow(1px 1px 2px ${dropShadow})`);
      }
      if (className) {
        textpath.setAttribute("class", className);
      }
      textpath.setAttribute("href", "#" + id);
      if (textAnchor === "middle") {
        textpath.setAttribute("startOffset", "50%");
      }
      textpath.setAttribute("fulltext", textContent);
      // if splitting two-word phrases across two lines and one of the words is too big, don't render anything
      if (words.length === 2 && wordsPerLine.length && wordsPerLine[0] === 1 && !subtext) {
        container.removeChild(groupContainer);
        return false;
      }
      textpath.textContent = subtext;
      text.appendChild(textpath);
      groupContainer.appendChild(text);

      // ensure text doesn't overflow textPaths
      if (textpath.getComputedTextLength() > textpathLength) {
        textpath.setAttribute("textLength", textpathLength);
        textpath.setAttribute("lengthAdjust", "spacingAndGlyphs");
      }
    }
  }
  return true;
}

export function createAggregateTextpath({
  id,
  container,
  textContent,
  fontSize,
  color,
  dropShadow,
  className,
  textpathLength,
  lines = 1,
  editable = false,
  eventID,
  fontFamily = 'Hand1-Regular',
}) {
  const groupContainer = document.createElementNS(svgNS, "g");
  if (container) {
    container.appendChild(groupContainer);
  }
  if (eventID) {
    groupContainer.id = "phrase-" + eventID;
    groupContainer.setAttribute("fulltext", textContent);
    groupContainer.addEventListener("click", () =>
      window.dispatchEvent(
        new CustomEvent("selectText", {
          detail: { text: textContent },
        })
      )
    );
  }
  const wordsPerLine = [];
  const words = textContent.split(" ");

  // make sure none of the words are individually longer than the shortest available textpath length
  const shortestTextpathLength = Math.min(...textpathLength);
  let safeFontSize = fontSize;
  const longestWord = Math.max(...words.map((word) => getTextDimensions(word + " ", safeFontSize).width));
  if (longestWord > shortestTextpathLength) {
    safeFontSize *= shortestTextpathLength / longestWord;
  }

  // Pre-compute word widths for balanced distribution across lines
  const wordWidths = words.map((word, idx) =>
    getTextDimensions(word + (idx === words.length - 1 ? "" : " "), safeFontSize).width
  );

  // Compute line break points (wordBreaks[i] = starting word index for line i)
  const wordBreaks = [0];
  if (lines > 1 && textpathLength) {
    // Greedy pass: fill each line to capacity
    let wIdx = 0;
    for (let i = 0; i < lines - 1; i++) {
      let lineLen = 0;
      while (wIdx < words.length && lineLen + (wordWidths[wIdx] ?? 0) <= textpathLength[i]) {
        lineLen += wordWidths[wIdx];
        wIdx++;
      }
      wordBreaks.push(wIdx);
    }

    // Rebalance: shift words from compressed lines to lighter adjacent lines
    const getLineWidth = (li) => {
      const start = wordBreaks[li];
      const end = li < lines - 1 ? wordBreaks[li + 1] : words.length;
      let w = 0;
      for (let j = start; j < end; j++) w += wordWidths[j];
      return w;
    };
    let shifted = true;
    while (shifted) {
      shifted = false;
      for (let i = lines - 1; i > 0; i--) {
        const currRatio = getLineWidth(i) / textpathLength[i];
        if (currRatio <= 1) continue;
        const prevRatio = getLineWidth(i - 1) / textpathLength[i - 1];
        if (currRatio <= prevRatio) continue;
        const lineEnd = i < lines - 1 ? wordBreaks[i + 1] : words.length;
        if (wordBreaks[i] + 1 >= lineEnd) continue;
        const oldMax = Math.max(currRatio, prevRatio);
        wordBreaks[i]++;
        const newMax = Math.max(getLineWidth(i) / textpathLength[i], getLineWidth(i - 1) / textpathLength[i - 1]);
        if (newMax < oldMax) {
          shifted = true;
        } else {
          wordBreaks[i]--;
        }
      }
    }

    // Dangler prevention: avoid single-word last line
    const lastLineWords = words.length - wordBreaks[lines - 1];
    if (lastLineWords === 1 && words.length > 3) {
      const prevStart = lines > 2 ? wordBreaks[lines - 2] : 0;
      if (wordBreaks[lines - 1] - prevStart > 1) {
        wordBreaks[lines - 1]--;
      }
    }
  }

  for (let i = 0; i < lines; i++) {
    const start = wordBreaks[i];
    const end = i < lines - 1 ? (wordBreaks[i + 1] ?? words.length) : words.length;
    const subtext = (lines === 1 || !textpathLength) ? textContent : words.slice(start, end).join(" ");
    wordsPerLine[i] = end - start;
    if (container && id) {
      const text = document.createElementNS(svgNS, "text");
      if (eventID) {
        text.addEventListener("click", () =>
          window.dispatchEvent(
            new CustomEvent("showPhraseOptions", {
              detail: { eventID },
            })
          )
        );
      }
      text.setAttribute("font-size", safeFontSize + "px");
      text.setAttribute("font-family", fontFamily);
      text.setAttribute("text-anchor", textAnchor);
      if (editable) {
        text.setAttribute("class", "editable");
      }
      groupContainer.setAttribute(
        "class",
        (editable ? "editable-textpath " : "") + (className ? className + "-container" : "")
      );
      const textpath = document.createElementNS(svgNS, "textPath");
      if (color) {
        textpath.setAttribute("fill", color);
      }
      if (dropShadow) {
        textpath.setAttribute("filter", `drop-shadow(1px 1px 2px ${dropShadow})`);
      }
      if (className) {
        textpath.setAttribute("class", className);
      }
      textpath.setAttribute("href", `#${id}-${i}`);
      if (textAnchor === "middle") {
        textpath.setAttribute("startOffset", "50%");
      }
      textpath.setAttribute("fulltext", textContent);
      // if splitting two-word phrases across two lines and one of the words is too big, don't render anything
      if (words.length === 2 && wordsPerLine.length && wordsPerLine[0] === 1 && !subtext) {
        if (container) {
          container.removeChild(groupContainer);
        }
        return false;
      }
      textpath.textContent = subtext;
      text.appendChild(textpath);
      groupContainer.appendChild(text);

      // ensure text doesn't overflow textPaths
      if (textpath.getComputedTextLength() > textpathLength[i]) {
        textpath.setAttribute("textLength", textpathLength[i]);
        textpath.setAttribute("lengthAdjust", "spacingAndGlyphs");
      }
    }
  }
  return true;
}
